/**
 * AWAC template registry.
 * ---------------------------------------------------------------------------
 * Maps an AWAC template name (the `template[].name` the agent emits in its
 * envelope) to the React component that renders it. To add a new adaptive-web
 * component, build the component and register it here — nothing else in the
 * client needs to change.
 *
 * Templates are code-split with React.lazy so the landing page and chat shell
 * don't pay for template code until the agent actually renders one. The lazy
 * wrappers exist synchronously, so name-gating via getAwacTemplate(name)
 * keeps working exactly as before — the GalleryPanel renders them inside a
 * Suspense boundary.
 */
import { lazy } from "react";
import { AWAC_CONSTANTS } from "../helpers/constants";

const REGISTRY = {
    [AWAC_CONSTANTS.Templates.FLOOR_PLAN_SHOWCASE]: lazy(() => import("./templates/FloorPlanShowcase")),
    [AWAC_CONSTANTS.Templates.UNIT_CAROUSEL]: lazy(() => import("./templates/UnitCarousel")),
    [AWAC_CONSTANTS.Templates.NEIGHBORHOOD_MAP]: lazy(() => import("./templates/NeighborhoodMap")),
    [AWAC_CONSTANTS.Templates.POI_MAP_CONTROL]: lazy(() => import("./templates/PoiMapControl")),
    [AWAC_CONSTANTS.Templates.BOOKING_FRAME]: lazy(() => import("./templates/BookingFrame")),
    [AWAC_CONSTANTS.Templates.BOOKING_SLOTS]: lazy(() => import("./templates/BookingSlots")),
    [AWAC_CONSTANTS.Templates.BOOKING_CONFIRM]: lazy(() => import("./templates/BookingConfirm"))
};

/**
 * Resolve a template component by name. Returns the lazy wrapper (truthy and
 * available synchronously), so callers can keep using this for name-gating.
 * @param {string} name
 * @returns {React.ComponentType|undefined}
 */
export function getAwacTemplate(name) {
    return name ? REGISTRY[name] : undefined;
}

export default REGISTRY;
